import { useState, useEffect } from 'react';
import axios from 'axios';
import { Server } from 'lucide-react';
import Sidebar from '@/components/Sidebar';

export const API = process.env.REACT_APP_BACKEND_URL;
export const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('discord_token')}` } });

export const inputCls = 'w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-3 py-2.5 text-[var(--text-primary)] text-sm font-[Manrope] placeholder-[var(--text-muted)] focus:outline-none focus:border-[#5865F2]/50';
export const labelCls = 'text-xs text-[var(--text-muted)] font-[Manrope] uppercase tracking-widest block mb-1.5';
export const cardCls = 'bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl';
export const btnPrimary = 'flex items-center gap-2 px-4 py-2 rounded-xl bg-[#5865F2] hover:bg-[#4752C4] text-white text-sm font-medium font-[Manrope] transition-colors disabled:opacity-50';

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  if (Number.isNaN(diff)) return '';
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

export function fmtDuration(secs) {
  if (!secs && secs !== 0) return '';
  const t = Math.max(0, Math.floor(secs));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

export function avatarUrl(userId, avatar) {
  if (!avatar) return null;
  if (String(avatar).startsWith('http')) return avatar;
  return `https://cdn.discordapp.com/avatars/${userId}/${avatar}.png?size=64`;
}

export function Avatar({ userId, avatar, name, size = 28 }) {
  const url = avatarUrl(userId, avatar);
  if (url) return <img src={url} alt="" style={{ width: size, height: size }} className="rounded-full flex-shrink-0 object-cover" />;
  return (
    <div style={{ width: size, height: size }} className="rounded-full bg-[#5865F2]/20 text-[#5865F2] flex items-center justify-center text-xs font-semibold flex-shrink-0">
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

export function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 disabled:opacity-40 ${checked ? 'bg-[#5865F2]' : 'bg-[var(--bg-hover)] border border-[var(--border-md)]'}`}
    >
      <span className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white shadow transition-all ${checked ? 'left-5' : 'left-1'}`} />
    </button>
  );
}

export function Spinner() {
  return <div className="w-10 h-10 border-2 border-[#5865F2] border-t-transparent rounded-full animate-spin" />;
}

export function PageLoading() {
  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Sidebar />
      <main className="lg:ml-60 pt-16 lg:pt-0 flex items-center justify-center min-h-screen"><Spinner /></main>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className={`${cardCls} p-8 text-center`}>
      <Icon size={32} className="text-[var(--text-muted)] mx-auto mb-3" />
      <p className="text-[var(--text-muted)] font-[Manrope] text-sm">{title}</p>
      {hint && <p className="text-[var(--text-muted)] font-[Manrope] text-xs mt-1">{hint}</p>}
    </div>
  );
}

export function PageShell({ icon: Icon, title, subtitle, actions, testId, isGlobal, maxW = 'max-w-3xl', children }) {
  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Sidebar />
      <main className="lg:ml-60 pt-16 lg:pt-0 p-6" data-testid={testId}>
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-[Outfit] text-[var(--text-primary)] flex items-center gap-3">
              <Icon size={28} className="text-[#5865F2]" />
              {title}
            </h1>
            <p className="text-[var(--text-muted)] text-sm mt-1 font-[Manrope]">{subtitle}</p>
          </div>
          {!isGlobal && actions}
        </div>
        {isGlobal ? (
          <div className={`${maxW} ${cardCls} p-8 text-center`}>
            <Server size={32} className="text-[var(--text-muted)] mx-auto mb-3" />
            <p className="text-[var(--text-muted)] font-[Manrope] text-sm">Select a specific server to use this page</p>
          </div>
        ) : (
          <div className={`${maxW} space-y-6`}>{children}</div>
        )}
      </main>
    </div>
  );
}

// Lists guild channels + roles using the same endpoints the Tickets/Settings pages use.
export function useChannelsAndRoles(guildId) {
  const [channels, setChannels] = useState([]);
  const [roles, setRoles] = useState([]);
  useEffect(() => {
    if (!guildId) return;
    let alive = true;
    Promise.allSettled([
      axios.get(`${API}/api/announce/channels/${guildId}`, authHeader()),
      axios.get(`${API}/api/reaction-roles/${guildId}/roles`, authHeader()),
    ]).then(([ch, ro]) => {
      if (!alive) return;
      if (ch.status === 'fulfilled') setChannels(ch.value.data.channels || []);
      if (ro.status === 'fulfilled') setRoles(ro.value.data.roles || []);
    });
    return () => { alive = false; };
  }, [guildId]);
  return { channels, roles };
}

// Multi-select of ids rendered as removable chips + a dropdown to add more.
export function IdPicker({ label, items, value, onChange, prefix = '', placeholder }) {
  const selected = value || [];
  const byId = Object.fromEntries(items.map(i => [i.id, i]));
  const available = items.filter(i => !selected.includes(i.id));
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {selected.length === 0 && <span className="text-[var(--text-muted)] text-xs font-[Manrope]">{placeholder}</span>}
        {selected.map(id => (
          <span key={id} className="inline-flex items-center gap-1 text-xs font-[Manrope] px-2 py-1 rounded-lg bg-[#5865F2]/15 text-[#5865F2] border border-[#5865F2]/25">
            {prefix}{byId[id]?.name || id}
            <button type="button" onClick={() => onChange(selected.filter(x => x !== id))} className="hover:text-red-400" aria-label="Remove">×</button>
          </span>
        ))}
      </div>
      <select value="" onChange={e => e.target.value && onChange([...selected, e.target.value])} className={inputCls}>
        <option value="">+ Add…</option>
        {available.map(i => <option key={i.id} value={i.id}>{prefix}{i.name}</option>)}
      </select>
    </div>
  );
}
